import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Info, Sparkles, AlertTriangle, Wrench, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/services/api';

const AnnouncementWidget = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    // Fetch active announcements
    const { data: announcements, isLoading } = useQuery({
        queryKey: ['userAnnouncements'],
        queryFn: async () => {
            const response = await api.get('/announcements/user/active');
            return response.data.announcements || [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Mark announcement as seen
    const markAsSeenMutation = useMutation({
        mutationFn: async (announcementId) => {
            await api.post(`/announcements/user/${announcementId}/seen`);
        }
    });

    // Show modal when announcements are loaded
    useEffect(() => {
        if (announcements && announcements.length > 0 && !isLoading) {
            setIsVisible(true);
        }
    }, [announcements, isLoading]);

    const typeConfig = {
        info: {
            icon: <Info className="h-6 w-6" />,
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-600',
            borderColor: 'border-blue-200',
            headerBg: 'bg-blue-100'
        },
        feature: {
            icon: <Sparkles className="h-6 w-6" />,
            bgColor: 'bg-purple-50',
            iconColor: 'text-purple-600',
            borderColor: 'border-purple-200',
            headerBg: 'bg-purple-100'
        },
        warning: {
            icon: <AlertTriangle className="h-6 w-6" />,
            bgColor: 'bg-yellow-50',
            iconColor: 'text-yellow-600',
            borderColor: 'border-yellow-200',
            headerBg: 'bg-yellow-100'
        },
        maintenance: {
            icon: <Wrench className="h-6 w-6" />,
            bgColor: 'bg-gray-50',
            iconColor: 'text-gray-600',
            borderColor: 'border-gray-200',
            headerBg: 'bg-gray-100'
        }
    };

    const handleClose = () => {
        // Mark all announcements as seen
        announcements.forEach(announcement => {
            markAsSeenMutation.mutate(announcement._id);
        });
        setIsVisible(false);
    };

    const handleNext = () => {
        if (currentIndex < announcements.length - 1) {
            // Mark current as seen before moving to next
            markAsSeenMutation.mutate(announcements[currentIndex]._id);
            setCurrentIndex(currentIndex + 1);
        } else {
            handleClose();
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    if (!isVisible || !announcements || announcements.length === 0) {
        return null;
    }

    const currentAnnouncement = announcements[currentIndex];
    const config = typeConfig[currentAnnouncement.type] || typeConfig.info;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-xl`}>
                {/* Header */}
                <div className={`${config.headerBg} ${config.borderColor} border-b px-6 py-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className={`${config.iconColor}`}>
                                {config.icon}
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {currentAnnouncement.title}
                                </h2>
                                {announcements.length > 1 && (
                                    <p className="text-sm text-gray-600 mt-1">
                                        Announcement {currentIndex + 1} of {announcements.length}
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className={`${config.bgColor} p-6`}>
                    <div className="prose prose-gray max-w-none">
                        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {currentAnnouncement.content}
                        </p>
                    </div>

                    {/* Priority Badge */}
                    <div className="mt-4 flex items-center space-x-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                            ${currentAnnouncement.priority === 'high'
                            ? 'bg-red-100 text-red-800'
                            : currentAnnouncement.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                        }`}>
                            {currentAnnouncement.priority.charAt(0).toUpperCase() + currentAnnouncement.priority.slice(1)} Priority
                        </span>
                        {currentAnnouncement.expiresAt && (
                            <span className="text-sm text-gray-600">
                                Expires: {new Date(currentAnnouncement.expiresAt).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        {currentAnnouncement.createdBy?.name && (
                            <span>From: {currentAnnouncement.createdBy.name}</span>
                        )}
                    </div>

                    <div className="flex items-center space-x-3">
                        {announcements.length > 1 && (
                            <>
                                <button
                                    onClick={handlePrevious}
                                    disabled={currentIndex === 0}
                                    className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium
                                        ${currentIndex === 0
                                        ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                    }`}
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Previous
                                </button>

                                {/* Page indicators */}
                                <div className="flex space-x-1">
                                    {announcements.map((_, index) => (
                                        <div
                                            key={index}
                                            className={`h-2 w-2 rounded-full ${
                                                index === currentIndex ? 'bg-primary-600' : 'bg-gray-300'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        <button
                            onClick={handleNext}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {currentIndex === announcements.length - 1 ? (
                                <>
                                    Got it
                                    <X className="h-4 w-4 ml-2" />
                                </>
                            ) : (
                                <>
                                    Next
                                    <ChevronRight className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementWidget;